/**
 * vehicle-garage.js
 * Ess K Customs — Vehicle Navigation System
 *
 * Architecture (refactored 2026-05-06 to mirror Enterprise's web-component
 * pattern; replaces previous single-IIFE structure):
 *
 *   ─── Module structure ──────────────────────────────────────────────
 *
 *   VehicleGarageStore      Storage + state. EventTarget — fires 'change'
 *                           events on set/clear so subscribers update.
 *                           Exposed at window.vehicleGarageStore.
 *
 *   <vehicle-garage-bar>    Custom element. Sitewide top banner showing
 *                           the selected vehicle. Auto-updates when store
 *                           changes. Defined here, used in
 *                           sections/vehicle-garage-bar.liquid.
 *
 *   <vehicle-fitment-badge> Custom element. Product-page fitment badge.
 *                           Reads server-rendered JSON island
 *                           (#vehicle-fitment-data) and the store, computes
 *                           compatibility state, renders accordingly.
 *
 *   Cart integration        Top-level handlers for cart-note write,
 *                           line-item property injection, and warning
 *                           chips for items bought for a different vehicle.
 *
 *   ─── About the fetch interceptor ──────────────────────────────────
 *
 *   We wrap window.fetch to inject _vehicle / _vehicle_slug / _universal
 *   properties on every /cart/add request. This is load-bearing: Enterprise's
 *   <product-form> only dispatches 'on:cart:add' AFTER a successful add, so
 *   there's no pre-submit hook where we could append properties without
 *   forking product-form.js. The alternatives are:
 *     a) Fork product-form.js — violates sections-only policy
 *     b) Post-add /cart/change to add properties — race window where items
 *        briefly exist in cart without the property
 *   So the fetch wrap stays. It's transparent (calls through to the original)
 *   and the only known risk — multiple wrappers chained — is functionally fine
 *   since each wrapper sees the previous one's wrapped version.
 *
 *   ─── Required HTML hooks ──────────────────────────────────────────
 *
 *   <vehicle-garage-bar>     — sections/vehicle-garage-bar.liquid
 *     #garage-bar-vehicle-link  Anchor populated with vehicle name + URL
 *     #garage-bar-clear         Button to clear selection
 *   <vehicle-fitment-badge>  — snippets/vehicle-fitment-data.liquid
 *   #vehicle-fitment-data      <script type="application/json"> island
 *   [data-vehicle-handle][data-vehicle-name]
 *   [data-vehicle-make][data-vehicle-model]
 *                              Vehicle cards in the navigator
 *
 *   Loaded from layout/theme.liquid (1 of the 3 sanctioned theme.liquid edits).
 */

'use strict';

{
  /* ─── Inline styles for elements that appear inside the cart drawer ─── */
  /* Cart drawer is global (overlay group, present on every page), but
     essk-design-tokens.css only loads on product pages. So chip styles must
     be inlined here so they're available wherever the cart drawer might open. */
  if (!document.getElementById('vehicle-garage-inline-styles')) {
    const style = document.createElement('style');
    style.id = 'vehicle-garage-inline-styles';
    style.textContent = `
      .vehicle-cart-chip {
        display: block;
        box-sizing: border-box;
        width: 100%;
        /* Enterprise's <li class="cart-item"> uses CSS grid (image | content
           columns) on mobile and flex on desktop. These overrides make the
           chip span full-width across whatever layout the parent uses. */
        grid-column: 1 / -1;
        flex-basis: 100%;
        margin-top: 0.6rem;
        padding: 0.5rem 0.8rem;
        border-radius: 0.5rem;
        background: rgba(220, 145, 30, 0.1);
        border: 0.1rem solid rgba(220, 145, 30, 0.3);
        color: #985d0a;
        font-family: var(--body-font-family);
        font-size: 1.2rem;
        line-height: 1.3;
      }
    `;
    document.head.appendChild(style);
  }

  /* ─── Constants ───────────────────────────────────────────────── */

  const STORAGE_KEY = 'essk_garage';

  /**
   * Legacy slug rewrites — when consolidating vehicle groups, map deprecated
   * handles to their successor so customers with the old slug saved keep
   * working. Add new entries when consolidating; format:
   *   { from: /^old-handle-pattern$/, to: 'new-handle', model: '...', name: '...' }
   */
  const LEGACY_SLUG_REWRITES = [
    // 2026-05-04: Ford Super Duty consolidation (F-250/F-350/F-450 → Super Duty)
    { from: /^ford-f-(250|350|450|550)-super-duty-1999-2007$/, to: 'ford-super-duty-1999-2007', model: 'Super Duty', name: '1999–2007 Ford Super Duty' },
    { from: /^ford-f-(250|350|450|550)-super-duty-2008-2010$/, to: 'ford-super-duty-2008-2010', model: 'Super Duty', name: '2008–2010 Ford Super Duty' },
    { from: /^ford-f-(250|350|450|550)-super-duty-2011-2016$/, to: 'ford-super-duty-2011-2016', model: 'Super Duty', name: '2011–2016 Ford Super Duty' },
    { from: /^ford-f-(250|350|450|550)-super-duty-2017-2022$/, to: 'ford-super-duty-2017-2022', model: 'Super Duty', name: '2017–2022 Ford Super Duty' },
    { from: /^ford-f-(250|350|450)-super-duty-2023-202[456]$/, to: 'ford-super-duty-2023-2026', model: 'Super Duty', name: '2023–2026 Ford Super Duty' },
    // 2026-05-04: Ford Transit consolidation (Transit-150/250/350/350-HD + pre-2015 dupes → Transit)
    { from: /^ford-transit-(150|250|350|350-hd)-2015-20(15|16|26)$/, to: 'ford-transit-2015-2026', model: 'Transit', name: '2015–2026 Ford Transit' },
    { from: /^ford-transit-(2010-2013|2014-2024)$/,                   to: 'ford-transit-2015-2026', model: 'Transit', name: '2015–2026 Ford Transit' },
    // 2026-05-04: Chevy Silverado 1500 HD/LD/LTD cleanup (LD/LTD were empty placeholders)
    { from: /^chevrolet-silverado-1500-hd-2001-2006$/,                 to: 'chevrolet-silverado-1500-1999-2007', model: 'Silverado 1500', name: '1999–2007 Chevrolet Silverado 1500' },
    { from: /^chevrolet-silverado-1500-ld-2019-2019$/,                 to: 'chevrolet-silverado-1500-2019-2026', model: 'Silverado 1500', name: '2019–2026 Chevrolet Silverado 1500' },
    { from: /^chevrolet-silverado-1500-ltd-2022-2022$/,                to: 'chevrolet-silverado-1500-2019-2026', model: 'Silverado 1500', name: '2019–2026 Chevrolet Silverado 1500' },
    // 2026-05-04: Silverado 2500/3500 consolidation
    { from: /^chevrolet-silverado-(2500|2500-hd|3500)-(1999|2001)-2007$/, to: 'chevrolet-silverado-2500-3500-1999-2007', model: 'Silverado 2500/3500', name: '1999–2007 Chevrolet Silverado 2500/3500' },
    { from: /^chevrolet-silverado-(2500|3500)-hd-2007-2014$/,          to: 'chevrolet-silverado-2500-3500-2007-2014', model: 'Silverado 2500/3500', name: '2007–2014 Chevrolet Silverado 2500/3500' },
    { from: /^chevrolet-silverado-(2500|3500)-hd-2015-2019$/,          to: 'chevrolet-silverado-2500-3500-2015-2019', model: 'Silverado 2500/3500', name: '2015–2019 Chevrolet Silverado 2500/3500' },
    { from: /^chevrolet-silverado-(2500|3500)-hd-2020-2026$/,          to: 'chevrolet-silverado-2500-3500-2020-2026', model: 'Silverado 2500/3500', name: '2020–2026 Chevrolet Silverado 2500/3500' },
    // 2026-05-04: Suburban consolidation (drop 1500/2500/3500 suffix)
    { from: /^chevrolet-suburban-1500-2000-2006$/,                     to: 'chevrolet-suburban-2000-2006', model: 'Suburban', name: '2000–2006 Chevrolet Suburban' },
    { from: /^chevrolet-suburban-(1500-2007-2014|2500-2007-2013|2014-2014)$/, to: 'chevrolet-suburban-2007-2014', model: 'Suburban', name: '2007–2014 Chevrolet Suburban' },
    { from: /^chevrolet-suburban-3500-hd-2016-2019$/,                  to: 'chevrolet-suburban-2015-2020', model: 'Suburban', name: '2015–2020 Chevrolet Suburban' },
    // 2026-05-04: Ram 2500/3500/4500 consolidation (2010+ Ram-branded only; Dodge Ram pre-2010 left alone)
    { from: /^ram-(2500|3500|4500)-2011-2018$/,                        to: 'ram-2500-3500-2010-2018', model: '2500/3500', name: '2010–2018 Ram 2500/3500' },
    { from: /^ram-(2500|3500)-2019-2026$/,                             to: 'ram-2500-3500-2019-2026', model: '2500/3500', name: '2019–2026 Ram 2500/3500' },
    { from: /^ram-4500-2019-2024$/,                                    to: 'ram-2500-3500-2019-2026', model: '2500/3500', name: '2019–2026 Ram 2500/3500' },
    // 2026-05-04: Ram ProMaster 1500/2500/3500 consolidation (ProMaster City untouched)
    { from: /^ram-promaster-(1500|2500|3500)-2014-2025$/,              to: 'ram-promaster-2014-2025', model: 'ProMaster', name: '2014–2025 Ram ProMaster' },
    // 2026-05-04: GMC Sierra 2500/3500 HD consolidation + Yukon XL → Yukon merge
    { from: /^gmc-sierra-(2500|3500)-hd-2007-2014$/,                   to: 'gmc-sierra-2500-3500-2007-2014', model: 'Sierra 2500/3500', name: '2007–2014 GMC Sierra 2500/3500' },
    { from: /^gmc-sierra-(2500|3500)-hd-2015-2019$/,                   to: 'gmc-sierra-2500-3500-2015-2019', model: 'Sierra 2500/3500', name: '2015–2019 GMC Sierra 2500/3500' },
    { from: /^gmc-sierra-(2500|3500)-hd-2020-2026$/,                   to: 'gmc-sierra-2500-3500-2020-2026', model: 'Sierra 2500/3500', name: '2020–2026 GMC Sierra 2500/3500' },
    { from: /^gmc-yukon-xl-(1500-2007-2014|2500-2007-2013)$/,          to: 'gmc-yukon-2007-2014', model: 'Yukon', name: '2007–2014 GMC Yukon' },
    { from: /^gmc-yukon-xl-2015-2020$/,                                to: 'gmc-yukon-2015-2020', model: 'Yukon', name: '2015–2020 GMC Yukon' },
    // 2026-05-04: Subaru WRX STI → WRX merge + XV Crosstrek → Crosstrek dedupe
    { from: /^subaru-wrx-sti-2013-2014$/,                              to: 'subaru-wrx-2008-2014', model: 'WRX', name: '2008–2014 Subaru WRX' },
    { from: /^subaru-wrx-sti-2015-2021$/,                              to: 'subaru-wrx-2015-2021', model: 'WRX', name: '2015–2021 Subaru WRX' },
    { from: /^subaru-xv-crosstrek-2013-2015$/,                         to: 'subaru-crosstrek-2013-2017', model: 'Crosstrek', name: '2013–2017 Subaru Crosstrek' },
    // 2026-05-06: Mercedes-Benz Sprinter 1500/2500/3500/3500XD consolidation
    { from: /^mercedes-benz-sprinter-(2500|3500)-2014-2018$/,          to: 'mercedes-benz-sprinter-2007-2018', model: 'Sprinter', name: '2007–2018 Mercedes-Benz Sprinter' },
    { from: /^mercedes-benz-sprinter-(1500|2500|3500|3500xd)-2019-2026$/, to: 'mercedes-benz-sprinter-2019-2026', model: 'Sprinter', name: '2019–2026 Mercedes-Benz Sprinter' },
    // 2026-05-06: Polaris RZR XP 4 → RZR XP merge (lighting fits both 2-seater XP and 4-seater XP 4)
    { from: /^polaris-rzr-xp-2011-2013$/,                              to: 'polaris-rzr-xp-2011-2014', model: 'RZR XP', name: '2011–2014 Polaris RZR XP' },
    { from: /^polaris-rzr-xp-4-2013-2014$/,                            to: 'polaris-rzr-xp-2011-2014', model: 'RZR XP', name: '2011–2014 Polaris RZR XP' },
    { from: /^polaris-rzr-xp-4-2014-2015$/,                            to: 'polaris-rzr-xp-2014-2015', model: 'RZR XP', name: '2014–2015 Polaris RZR XP' },
    { from: /^polaris-rzr-xp-4-2016-2019$/,                            to: 'polaris-rzr-xp-2016-2021', model: 'RZR XP', name: '2016–2021 Polaris RZR XP' },
    { from: /^polaris-rzr-xp-4-2020-2023$/,                            to: 'polaris-rzr-xp-2020-2024', model: 'RZR XP', name: '2020–2024 Polaris RZR XP' }
  ];

  /* ─── Storage layer ───────────────────────────────────────────── */

  /**
   * Storage + state container. Subscribers listen via:
   *   store.addEventListener('change', () => { ... });
   * `change` events fire on set() and clear().
   */
  class VehicleGarageStore extends EventTarget {
    get() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? this._applyLegacyRewrite(JSON.parse(raw)) : null;
      } catch (e) {
        return null;
      }
    }

    set({ handle, name, collectionUrl, make, model }) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          handle,
          name,
          collectionUrl,
          make:    make  || '',
          model:   model || '',
          savedAt: Date.now()
        }));
        this.dispatchEvent(new CustomEvent('change', { detail: { vehicle: this.get() } }));
      } catch (e) {
        // Private browsing / storage full — fail silently.
      }
    }

    clear() {
      try {
        localStorage.removeItem(STORAGE_KEY);
        this.dispatchEvent(new CustomEvent('change', { detail: { vehicle: null } }));
      } catch (e) {}
    }

    _applyLegacyRewrite(garage) {
      if (!garage || !garage.handle) return garage;
      for (const rule of LEGACY_SLUG_REWRITES) {
        if (rule.from.test(garage.handle)) {
          garage.handle = rule.to;
          garage.model = rule.model;
          garage.name = rule.name;
          garage.collectionUrl = '/collections/' + rule.to;
          try { localStorage.setItem(STORAGE_KEY, JSON.stringify(garage)); } catch (e) {}
          return garage;
        }
      }
      return garage;
    }
  }

  const store = new VehicleGarageStore();
  window.vehicleGarageStore = store;  // expose for debugging / external integrations

  /* ─── <vehicle-garage-bar> custom element ────────────────────── */

  class VehicleGarageBar extends HTMLElement {
    connectedCallback() {
      this.linkEl  = this.querySelector('#garage-bar-vehicle-link');
      this.clearEl = this.querySelector('#garage-bar-clear');

      this.update();

      if (this.clearEl) {
        this.clearEl.addEventListener('click', () => {
          store.clear();
          // Cart chips must clear immediately even if no cart-drawer event fires.
          removeAllCartChips();
        });
      }

      this._onChange = () => this.update();
      store.addEventListener('change', this._onChange);
    }

    disconnectedCallback() {
      store.removeEventListener('change', this._onChange);
    }

    update() {
      const v = store.get();
      if (v && this.linkEl) {
        this.linkEl.textContent = v.name;
        this.linkEl.href = v.collectionUrl;
        this.hidden = false;
        this.setAttribute('aria-live', 'polite');
      } else {
        this.hidden = true;
        this.removeAttribute('aria-live');
      }
    }
  }
  customElements.define('vehicle-garage-bar', VehicleGarageBar);

  /* ─── <vehicle-fitment-badge> custom element ─────────────────── */

  class VehicleFitmentBadge extends HTMLElement {
    connectedCallback() {
      this._reparentToProductForm();
      this.update();
      this._onChange = () => this.update();
      store.addEventListener('change', this._onChange);
    }

    disconnectedCallback() {
      store.removeEventListener('change', this._onChange);
    }

    /**
     * theme.liquid renders this snippet between </main> and the overlay/footer
     * sections — way at the bottom of the body where customers won't see it.
     * On product pages, move ourselves to right above the <product-form> so
     * the fitment confirmation/warning is adjacent to the Add to Cart button.
     * No-op when there's no <product-form> on the page (e.g. cart, search).
     */
    _reparentToProductForm() {
      const productForm = document.querySelector('product-form');
      if (!productForm || !productForm.parentNode) return;
      if (this.parentNode === productForm.parentNode && this.nextSibling === productForm) return;
      productForm.parentNode.insertBefore(this, productForm);
    }

    update() {
      const data = this._getFitmentData();
      if (!data) {
        this.hidden = true;
        return;
      }
      const state = this._computeState(data, store.get());
      if (state.kind === 'hidden') {
        this.hidden = true;
        return;
      }
      this.className = 'vehicle-fitment-badge vehicle-fitment-badge--' + state.kind;
      this.innerHTML =
        `<span class="vehicle-fitment-badge__icon" aria-hidden="true">${state.icon}</span>` +
        `<span class="vehicle-fitment-badge__body">` +
          `<span class="vehicle-fitment-badge__label">${state.label}</span>` +
          `<span class="vehicle-fitment-badge__text">${state.text}</span>` +
          (state.note ? `<span class="vehicle-fitment-badge__note">${state.note}</span>` : '') +
        `</span>`;
      this.hidden = false;
    }

    _getFitmentData() {
      const script = document.getElementById('vehicle-fitment-data');
      if (!script) return null;
      try { return JSON.parse(script.textContent); } catch (e) { return null; }
    }

    _computeState(data, garage) {
      if (data.is_universal_fit === true) {
        return { kind: 'universal', icon: '✓',
                 label: 'Universal fit',
                 text: 'Fits any vehicle',
                 note: data.fitment_notes ? escapeHtml(data.fitment_notes) : '' };
      }
      if (!garage || !garage.handle) {
        return { kind: 'hidden' };
      }
      const inCollection = (data.vehicle_handles || []).indexOf(garage.handle) !== -1;
      const vehicleName = escapeHtml(garage.name);

      if (inCollection && data.requires_additional_parts === true) {
        return { kind: 'adapter', icon: '⚠',
                 label: 'Compatible — adapter required',
                 text: `Fits your ${vehicleName} with the included or required adapter`,
                 note: data.fitment_notes ? escapeHtml(data.fitment_notes) : '' };
      }
      if (inCollection) {
        return { kind: 'compatible', icon: '✓',
                 label: 'Compatible',
                 text: `Fits your ${vehicleName}`,
                 note: data.fitment_notes ? escapeHtml(data.fitment_notes) : '' };
      }
      return { kind: 'incompatible', icon: '✗',
               label: 'Not compatible',
               text: `Does not fit your ${vehicleName}`,
               note: '' };
    }
  }
  customElements.define('vehicle-fitment-badge', VehicleFitmentBadge);

  /* ─── Vehicle card click handling ────────────────────────────── */

  /**
   * Delegated click handler for vehicle cards rendered anywhere on the site.
   * Cards must carry: data-vehicle-handle, data-vehicle-name,
   * data-vehicle-make, data-vehicle-model, plus an href (on the element or
   * a child <a>). Navigation continues naturally via the link.
   */
  document.addEventListener('click', (e) => {
    const card = e.target.closest('[data-vehicle-handle]');
    if (!card) return;

    const handle = card.dataset.vehicleHandle;
    const name   = card.dataset.vehicleName;
    const make   = card.dataset.vehicleMake;
    const model  = card.dataset.vehicleModel;
    const url    = card.getAttribute('href') ||
                   (card.querySelector('a') && card.querySelector('a').getAttribute('href'));

    if (!handle || !name || !url) return;

    store.set({ handle, name, collectionUrl: url, make, model });
    writeVehicleToCart();
    saveVehicleToCustomerAccount(store.get());
  });

  /* ─── Cart attributes (Convermax-style "Additional details") ───── */

  /**
   * Writes vehicle as structured cart attributes (Year / Make / Model / Vehicle).
   * Carries over to the order as customAttributes — displays under
   * "Additional details" in Shopify admin order view (same place Convermax
   * used). Replaces an earlier approach that wrote to cart note, which
   * overwrote any customer-supplied note text.
   *
   * Year here is the generation year-range (e.g. "2021–2026"), since our
   * vehicle picker is generation-based; Convermax used a single year because
   * its picker was year-based. Range is parsed from the leading token of the
   * display name (e.g. "2021–2026 Ford F-150" → "2021–2026").
   */
  function writeVehicleToCart() {
    const garage = store.get();
    if (!garage) return;
    const yearMatch = (garage.name || '').match(/^(\S+)/);
    const yearRange = yearMatch ? yearMatch[1] : '';
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            Year: yearRange,
            Make: garage.make || '',
            Model: garage.model || '',
            Vehicle: garage.name || ''
          }
        })
      });
    } catch (e) { /* Cart attributes are non-critical. */ }
  }

  /* ─── Cart line-item property injection ─────────────────────── */

  /**
   * For non-AJAX form submissions: append hidden inputs.
   * The underscore prefix (_vehicle, _vehicle_slug) hides the property from
   * the storefront cart UI but keeps it visible in admin / on orders.
   */
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form || form.getAttribute('action') !== '/cart/add') return;

    const garage = store.get();
    if (!garage) return;

    upsertHiddenInput(form, 'properties[_vehicle]',      garage.name);
    upsertHiddenInput(form, 'properties[_vehicle_slug]', garage.handle);
  });

  function upsertHiddenInput(form, name, value) {
    let input = form.querySelector(`[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  /**
   * Wraps window.fetch to inject _vehicle / _vehicle_slug / _universal
   * properties on every /cart/add request. See header comment for why
   * this monkey-patch is load-bearing.
   *
   * Handles all three body formats Shopify themes use: FormData,
   * JSON string, URLSearchParams.
   */
  (function installFetchInterceptor() {
    if (!window.fetch) return;
    const originalFetch = window.fetch;
    window.fetch = function (input, init) {
      const url = (typeof input === 'string') ? input : (input && input.url) || '';
      if (url.indexOf('/cart/add') !== -1) {
        const garage = store.get();
        if (garage && init && init.body) {
          try {
            init.body = injectIntoBody(init.body, init.headers, garage);
          } catch (e) { /* Let original request proceed unchanged. */ }
        }
      }
      return originalFetch.apply(this, arguments);
    };
  })();

  /**
   * Reads the server-rendered fitment JSON island for the current product.
   * Returns null if the page isn't a product page (no JSON to read).
   */
  function getCurrentProductFitment() {
    try {
      const script = document.getElementById('vehicle-fitment-data');
      if (!script) return null;
      const data = JSON.parse(script.textContent);
      return {
        universal: data.is_universal_fit === true ? 'yes' : 'no',
        vehicleHandles: Array.isArray(data.vehicle_handles) ? data.vehicle_handles : []
      };
    } catch (e) { return null; }
  }

  function injectIntoBody(body, headers, garage) {
    const fitment = getCurrentProductFitment();
    const universal = fitment ? fitment.universal : null;
    // Comma-separated list of vehicle handles the product fits. Empty for
    // products on non-product pages (where the JSON island doesn't render).
    const productVehicles = (fitment && fitment.vehicleHandles.length > 0)
      ? fitment.vehicleHandles.join(',')
      : null;

    function appendAll(append) {
      append('properties[_vehicle]',      garage.name);
      append('properties[_vehicle_slug]', garage.handle);
      if (universal !== null)        append('properties[_universal]',         universal);
      if (productVehicles !== null)  append('properties[_product_vehicles]',  productVehicles);
    }

    // FormData
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      appendAll((k, v) => body.append(k, v));
      return body;
    }

    // JSON string
    let ct = '';
    if (headers) {
      if (typeof headers.get === 'function') ct = headers.get('Content-Type') || headers.get('content-type') || '';
      else ct = headers['Content-Type'] || headers['content-type'] || '';
    }

    if (typeof body === 'string') {
      if (ct.indexOf('application/json') !== -1 || /^\s*[{[]/.test(body)) {
        try {
          const data = JSON.parse(body);
          if (!data.properties || typeof data.properties !== 'object') data.properties = {};
          data.properties['_vehicle']      = garage.name;
          data.properties['_vehicle_slug'] = garage.handle;
          if (universal !== null)       data.properties['_universal']         = universal;
          if (productVehicles !== null) data.properties['_product_vehicles']  = productVehicles;
          return JSON.stringify(data);
        } catch (e) { /* fall through to urlencoded */ }
      }
      const params = new URLSearchParams(body);
      appendAll((k, v) => params.append(k, v));
      return params.toString();
    }

    // URLSearchParams
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      appendAll((k, v) => body.append(k, v));
      return body;
    }

    return body;
  }

  /* ─── Customer Account API ──────────────────────────────────── */

  /**
   * Save the selected vehicle to the logged-in customer's metafields.
   * Silently no-ops for guests.
   */
  function saveVehicleToCustomerAccount(garage) {
    if (!garage) return;
    const customerId = window.Shopify && window.Shopify.customer && window.Shopify.customer.id;
    if (!customerId) return;

    const mutation = `
      mutation customerMetafieldsSet($metafields: [MetafieldsSetInput!]!) {
        customerMetafieldsSet(metafields: $metafields) {
          metafields { key namespace value }
          userErrors { field message }
        }
      }`;

    const variables = {
      metafields: [
        { namespace: 'custom', key: 'primary_vehicle',      value: garage.name,            type: 'single_line_text_field' },
        { namespace: 'custom', key: 'primary_vehicle_slug', value: garage.handle,          type: 'single_line_text_field' },
        { namespace: 'custom', key: 'vehicle_make',         value: garage.make  || '',     type: 'single_line_text_field' },
        { namespace: 'custom', key: 'vehicle_model',        value: garage.model || '',     type: 'single_line_text_field' }
      ]
    };

    try {
      fetch('/account/customer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: mutation, variables: variables })
      });
    } catch (e) { /* Non-critical. */ }
  }

  /* ─── Cart drawer warning chips ─────────────────────────────── */

  /**
   * Inspects /cart.js, finds line items whose _vehicle_slug differs from the
   * currently-selected vehicle, and adds a "⚠ For [other vehicle]" chip to
   * each such line item in the cart drawer.
   *
   * Triggered by Enterprise's own cart events — replaces the previous
   * MutationObserver approach.
   */
  function inspectCartAndWarn() {
    const garage = store.get();
    if (!garage || !garage.handle) {
      removeAllCartChips();
      return;
    }
    fetch('/cart.js', { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((cart) => annotateCartLineItems(cart, garage))
      .catch(() => { /* non-critical */ });
  }

  /**
   * Decides whether a cart line item should get a "doesn't fit" warning chip.
   *
   * Two checks, in priority order:
   *   1. _product_vehicles (set by the fetch interceptor at add-time, reads the
   *      product's actual fitment from the JSON island): authoritative — a
   *      product fits if its handle list contains the current garage's handle.
   *      This catches the "user adds non-fitting product" case.
   *   2. _vehicle_slug (legacy fallback): for items added before _product_vehicles
   *      existed, fall back to "did the user's selected vehicle change since
   *      this was added?". Less precise — doesn't catch the "added wrong product
   *      for the selected vehicle" case — but better than nothing.
   *
   * Universal items (`_universal: yes`) never warn.
   */
  function annotateCartLineItems(cart, garage) {
    if (!cart || !cart.items) return;
    // Always start from a clean slate — items may have been removed/reordered
    // since the last inspection.
    removeAllCartChips();
    cart.items.forEach((item, idx) => {
      const props = item.properties || {};
      if (props._universal === 'yes') return;

      let mismatch = false;

      if (props._product_vehicles) {
        const handles = props._product_vehicles.split(',').filter(Boolean);
        if (handles.length > 0 && !handles.includes(garage.handle)) {
          mismatch = true;
        }
      } else if (props._vehicle_slug && props._vehicle_slug !== garage.handle) {
        mismatch = true;
      }

      if (mismatch) {
        // line index is 1-based in Enterprise's DOM (#cart-item-1, #cart-item-2, ...)
        addCartChipToLineItem(item, idx + 1, garage.name);
      }
    });
  }

  /**
   * Find the cart line item DOM element. Enterprise renders line items with
   * id="cart-item-{line}" where line is the 1-based position in the cart.
   * Confirmed by reading cart-items.js (search for `cart-item-${line}`).
   * Fall back to data-variant-id match if the ID lookup fails.
   */
  function findLineItemElement(item, lineIndex) {
    return document.getElementById(`cart-item-${lineIndex}`) ||
           document.querySelector(`[data-variant-id="${item.variant_id || item.id}"]`);
  }

  function addCartChipToLineItem(item, lineIndex, currentVehicleName) {
    const el = findLineItemElement(item, lineIndex);
    if (!el || el.querySelector('.vehicle-cart-chip')) return;

    const chip = document.createElement('div');
    chip.className = 'vehicle-cart-chip';
    chip.textContent = `⚠ Not compatible with your ${currentVehicleName || 'selected vehicle'}`;
    el.appendChild(chip);
  }

  function removeAllCartChips() {
    document.querySelectorAll('.vehicle-cart-chip').forEach((c) => c.remove());
  }

  // Triggers for re-inspecting the cart and updating chips:
  //   1. Enterprise's <product-form> dispatches 'on:cart:add' / 'on:line-item:change'
  //      after AJAX adds; both bubble up to document.
  //   2. Generic 'cart:refresh' for our own snippets (essk-product-addons) and
  //      any third-party listeners.
  //   3. Store 'change' — when the user picks a different vehicle, cart items
  //      that previously matched may no longer match (and vice-versa).
  //   4. MutationObserver on <cart-drawer> — covers the cases neither Enterprise
  //      nor we dispatch for: drawer opening with pre-existing items, qty
  //      changes inside the drawer (handled by Enterprise's <cart-items> which
  //      doesn't dispatch a public event), item removal. Enterprise inspects
  //      the drawer the same way (its dispatch:cart-drawer:refresh is for
  //      EXTERNAL code to ASK for a refresh; the drawer itself doesn't dispatch
  //      a "refreshed" event we could listen for).
  document.addEventListener('cart:refresh',         inspectCartAndWarn);
  document.addEventListener('on:cart:add',          inspectCartAndWarn);
  document.addEventListener('on:line-item:change',  inspectCartAndWarn);
  store.addEventListener('change',                  inspectCartAndWarn);

  (function watchCartDrawer() {
    let observed = false;
    function tryAttach() {
      if (observed) return;
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      const observer = new MutationObserver(() => {
        clearTimeout(observer._debounce);
        observer._debounce = setTimeout(inspectCartAndWarn, 150);
      });
      observer.observe(drawer, { childList: true, subtree: true });
      observed = true;
    }
    tryAttach();
    let attempts = 0;
    const retryInterval = setInterval(() => {
      tryAttach();
      if (observed || ++attempts > 20) clearInterval(retryInterval);
    }, 250);
  })();

  /* ─── Utility ────────────────────────────────────────────────── */

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, (c) =>
      ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'})[c]
    );
  }

  /* ─── Initial state on first load ─────────────────────────────── */

  function init() {
    const garage = store.get();
    if (garage) writeVehicleToCart();
    inspectCartAndWarn();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
